
import React, { useState, useEffect } from 'react';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { internalProfileService } from '@/services/internalProfileService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Save, Camera, Video, Palette, Users, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CreativeProfileForm: React.FC = () => {
  const { user, profile, refreshProfile } = useInternalAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    creative_role: '',
    portfolio_link: '',
    creative_specializations: '',
    equipment_used: '',
    creative_availability: '',
    years_creative_experience: '',
    creative_certifications: '',
    notable_projects: '',
    show_profile_public: false,
    show_contact_public: false,
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        creative_role: profile.creative_role || '',
        portfolio_link: profile.portfolio_link || '',
        creative_specializations: profile.creative_specializations || '',
        equipment_used: profile.equipment_used || '',
        creative_availability: profile.creative_availability || '',
        years_creative_experience: profile.years_creative_experience?.toString() || '',
        creative_certifications: profile.creative_certifications || '',
        notable_projects: profile.notable_projects || '',
        show_profile_public: profile.show_profile_public || false,
        show_contact_public: profile.show_contact_public || false,
      });
    }
  }, [profile]);

  const handleInputChange = (name: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error('No user found. Please log in again.');
      return;
    }

    setLoading(true);
    
    try {
      console.log('Submitting creative profile for user:', user.id);
      
      const profileData = {
        user_id: user.id,
        role: user.role,
        ...formData,
        years_creative_experience: formData.years_creative_experience ? parseInt(formData.years_creative_experience) : null,
      };

      console.log('Profile data to save:', profileData);

      const result = await internalProfileService.upsertProfile(profileData);

      if (result.success) {
        toast.success('Creative profile saved successfully!');
        console.log('Profile saved successfully, refreshing profile data');
        await refreshProfile();
        // Navigate back to profile page after a short delay
        setTimeout(() => {
          navigate('/internal-profile');
        }, 1000);
      } else {
        console.error('Failed to save profile:', result.error);
        toast.error(result.error || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'photographer': return <Camera className="h-4 w-4" />;
      case 'videographer': return <Video className="h-4 w-4" />;
      case 'designer': return <Palette className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="mb-6">
        <Button 
          onClick={() => navigate('/internal-profile')}
          variant="outline" 
          className="border-gray-600 text-gray-400 hover:bg-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Profile
        </Button>
      </div>

      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            {getRoleIcon(formData.creative_role)}
            Creative Profile
          </CardTitle>
          <CardDescription className="text-gray-400">
            Showcase your creative skills and experience to connect with potential clients and collaborators.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information - Read Only */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Username</Label>
                <Input 
                  value={user?.username || ''} 
                  disabled 
                  className="bg-gray-700 border-gray-600 text-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">Role</Label>
                <Input 
                  value={user?.role || ''} 
                  disabled 
                  className="bg-gray-700 border-gray-600 text-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-300">Age Group</Label>
                <Input 
                  value={user?.age_group || ''} 
                  disabled 
                  className="bg-gray-700 border-gray-600 text-gray-400"
                />
              </div>
            </div>

            {/* Creative Role Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Creative Role Information</h3>
              
              <div>
                <Label htmlFor="creative_role" className="text-gray-300">Creative Role *</Label>
                <Select 
                  value={formData.creative_role} 
                  onValueChange={(value) => handleInputChange('creative_role', value)}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select your creative role" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    <SelectItem value="photographer" className="text-white">
                      <div className="flex items-center gap-2">
                        <Camera className="h-4 w-4" />
                        Photographer
                      </div>
                    </SelectItem>
                    <SelectItem value="videographer" className="text-white">
                      <div className="flex items-center gap-2">
                        <Video className="h-4 w-4" />
                        Videographer
                      </div>
                    </SelectItem>
                    <SelectItem value="designer" className="text-white">
                      <div className="flex items-center gap-2">
                        <Palette className="h-4 w-4" />
                        Designer
                      </div>
                    </SelectItem>
                    <SelectItem value="other" className="text-white">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Other
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="portfolio_link" className="text-gray-300">Portfolio Link</Label>
                <Input
                  id="portfolio_link"
                  type="url"
                  value={formData.portfolio_link}
                  onChange={(e) => handleInputChange('portfolio_link', e.target.value)}
                  placeholder="https://your-portfolio.com"
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                />
              </div>

              <div>
                <Label htmlFor="creative_specializations" className="text-gray-300">Specializations</Label>
                <Textarea
                  id="creative_specializations"
                  value={formData.creative_specializations}
                  onChange={(e) => handleInputChange('creative_specializations', e.target.value)}
                  placeholder="Describe your areas of specialization..."
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400 min-h-[100px]"
                />
              </div>

              <div>
                <Label htmlFor="equipment_used" className="text-gray-300">Equipment Used</Label>
                <Textarea
                  id="equipment_used"
                  value={formData.equipment_used}
                  onChange={(e) => handleInputChange('equipment_used', e.target.value)}
                  placeholder="List your tools and equipment..."
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400 min-h-[100px]"
                />
              </div>

              <div>
                <Label htmlFor="creative_availability" className="text-gray-300">Availability</Label>
                <Textarea
                  id="creative_availability"
                  value={formData.creative_availability}
                  onChange={(e) => handleInputChange('creative_availability', e.target.value)}
                  placeholder="Describe your availability for projects..."
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
            </div>

            {/* Experience and Expertise */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Experience & Expertise</h3>
              
              <div>
                <Label htmlFor="years_creative_experience" className="text-gray-300">Years of Experience</Label>
                <Input
                  id="years_creative_experience"
                  type="number"
                  min="0"
                  max="50"
                  value={formData.years_creative_experience}
                  onChange={(e) => handleInputChange('years_creative_experience', e.target.value)}
                  placeholder="Number of years"
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                />
              </div>

              <div>
                <Label htmlFor="creative_certifications" className="text-gray-300">Certifications & Qualifications</Label>
                <Textarea
                  id="creative_certifications"
                  value={formData.creative_certifications}
                  onChange={(e) => handleInputChange('creative_certifications', e.target.value)}
                  placeholder="List your relevant certifications, training, or qualifications..."
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                />
              </div>

              <div>
                <Label htmlFor="notable_projects" className="text-gray-300">Notable Projects</Label>
                <Textarea
                  id="notable_projects"
                  value={formData.notable_projects}
                  onChange={(e) => handleInputChange('notable_projects', e.target.value)}
                  placeholder="Describe significant past projects or collaborations..."
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400 min-h-[120px]"
                />
              </div>
            </div>

            {/* Privacy Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Privacy Settings</h3>
              
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-white">Make Creative Profile Public</Label>
                  <p className="text-sm text-gray-400">
                    Allow others to view your portfolio and creative work
                  </p>
                </div>
                <Switch
                  checked={formData.show_profile_public}
                  onCheckedChange={(checked) => handleInputChange('show_profile_public', checked)}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-white">Show Contact Information Publicly</Label>
                  <p className="text-sm text-gray-400">
                    Display your contact info for potential clients
                  </p>
                </div>
                <Switch
                  checked={formData.show_contact_public}
                  onCheckedChange={(checked) => handleInputChange('show_contact_public', checked)}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-6">
              <Button
                type="submit"
                disabled={loading}
                className="bg-sports-green text-black hover:bg-green-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Creative Profile
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreativeProfileForm;
